import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Loader2, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ChangePasswordDialog = ({ open, onPasswordChanged }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validations
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Tous les champs sont obligatoires');
      return;
    }

    if (newPassword.length < 8) {
      setError('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (currentPassword === newPassword) {
      setError('Le nouveau mot de passe doit être différent de l\'ancien');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });

      toast.success('✅ Mot de passe changé avec succès!');
      
      // Réinitialiser le formulaire
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Notifier le parent que le mot de passe a été changé
      if (onPasswordChanged) {
        onPasswordChanged();
      }
    } catch (error) {
      console.error('Error changing password:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors du changement de mot de passe';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-orange-600" />
            Changement de Mot de Passe Obligatoire
          </DialogTitle>
          <DialogDescription>
            Vous devez changer votre mot de passe temporaire avant de continuer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="current-password">
              Mot de passe actuel <span className="text-red-500">*</span>
            </Label>
            <PasswordInput
              id="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="EduScope2024!"
              required
              disabled={loading}
            />
            <p className="text-xs text-gray-500">
              Saisissez le mot de passe temporaire qui vous a été fourni
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-password">
              Nouveau mot de passe <span className="text-red-500">*</span>
            </Label>
            <PasswordInput
              id="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 8 caractères"
              required
              minLength={8}
              disabled={loading}
            />
            <p className="text-xs text-gray-500">
              Minimum 8 caractères, utilisez un mot de passe sécurisé
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password">
              Confirmer le nouveau mot de passe <span className="text-red-500">*</span>
            </Label>
            <PasswordInput
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-saisissez le mot de passe"
              required
              minLength={8}
              disabled={loading}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Conseils pour un mot de passe sécurisé:</strong>
            </p>
            <ul className="text-xs text-blue-700 mt-2 space-y-1 ml-4">
              <li>• Au moins 8 caractères</li>
              <li>• Mélangez majuscules, minuscules, chiffres</li>
              <li>• Ajoutez des caractères spéciaux (@, !, #, etc.)</li>
              <li>• Évitez les mots du dictionnaire</li>
            </ul>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Changement en cours...
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Changer le Mot de Passe
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
